import React, { useState, useEffect } from 'react';
import { Alert, Button, Col, DatePicker, Modal, Row, notification } from 'antd';
import moment from 'moment';
import { getHostWithNoInvoice, getInoivceGraphData, getInvoicesByDate } from 'utils/apis/billingInvoice';
import { buildAPBillingInvoiceUri, buildAPInvoiceDashboard } from 'utils/routes';
import StatisticsCard from './components/StatisticsCard';
import TotalBilledCollectedGraph from './components/TotalBilledCollectedGraph';
import { BodyContainer, MainContainer } from './InvoiceDashboard.styles';
import ProductsTable from './components/ProductsTable';
import AdditionalProductGraph from './components/AdditionalProductGraph';
import { getAllProduct } from 'utils/apis/configProduct';

// Moved InvoiceBarChart inside InvoiceDashboard
const InvoiceDashboard = ({ history, location }) => {
  const [currentYearWithMonth, setCurrentYearWithMonth] = useState(moment().format('YYYY-MM'));
  const [endYearWithMonth, setEndYearWithMonth] = useState(moment().format('YYYY-MM'));
  const [invoiceStatisticsData, setInvoiceStatisticsData] = useState([]);
  const [invoiceAlert, setInvoiceAlert] = useState(false);
  const [monthlyHostsWithoutInvoices, setMonthlyHostsWithoutInvoices] = useState([]);
  const [yearlyHostsWithoutInvoices, setYearlyHostsWithoutInvoices] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(['month', 'month']);

  const [invoiceGraphData, setInvoiceGraphData] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const disallowedCategories = ['system', 'hostProtect', 'subscriptionAddOn'];
  const INTEGRATION_PRODUCT_STATS_SUFFIX = ' (Integration)';

  const catalogProductHasIntegrationParent = product =>
    product && Array.isArray(product.categoryParent) && product.categoryParent.includes('integration');
  // Handle URL query update
  const setURLQuery = (startDate, endDate) => {
    const queryString = `startDate=${startDate}&endDate=${endDate}`;
    history.replace({
      pathname: buildAPInvoiceDashboard(),
      search: queryString
    });
  };
  const handlePanelChange = dates => {
    if (dates && dates.length === 2) {
      const startYearWithMonth = moment(dates[0]).format('YYYY-MM');
      const endYearWithMonth = moment(dates[1]).format('YYYY-MM');

      // Calculate the difference in months between start and end
      const monthsDiff = Math.abs(moment(dates[1]).diff(moment(dates[0]), 'months', true));

      // Check if the range exceeds 3 months
      if (monthsDiff > 2) {
        // Show error notification
        notification.error({
          message: 'Invalid Date Range',
          description: 'Please select a date range within 3 months. The current selection exceeds the maximum allowed range.',
          duration: 4,
          placement: 'topRight'
        });

        // Don't update the state - keep the previous valid selection
        return;
      }

      // If validation passes, update the state
      setCurrentYearWithMonth(startYearWithMonth);
      setEndYearWithMonth(endYearWithMonth);
      setURLQuery(startYearWithMonth, endYearWithMonth);
      setInvoiceAlert(false);
      setMonthlyHostsWithoutInvoices([]);
      setYearlyHostsWithoutInvoices([]);
    }
    // Don't close the picker automatically - let user close it by clicking outside
  };

  const invoiceOfCurrentMonth = (invoices, startMonth, endMonth) => {
    const startOfRange = moment(startMonth, 'YYYY-MM')
      .startOf('month')
      .format('YYYY-MM-DD');
    const endOfRange = moment(endMonth, 'YYYY-MM')
      .endOf('month')
      .format('YYYY-MM-DD');
    return invoices.filter(invoice => invoice.invoiceDate >= startOfRange && invoice.invoiceDate <= endOfRange);
  };

  const totalInvoiceAmount = invoices => invoices.reduce((acc, invoice) => acc + parseFloat(invoice.amount), 0);

  const validatePaidStatus = invoices => invoices.filter(invoice => invoice.paymentStatus === 'paid');

  const validateInvOR = invoices => invoices.filter(invoice => invoice.officialInvoice.length > 0 && invoice.officialReceipt.length > 0);

  const allHostsWithoutInvoices = [...monthlyHostsWithoutInvoices, ...yearlyHostsWithoutInvoices];

  // Open modal when alert is clicked
  const handleAlertClick = () => {
    setIsModalVisible(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleTodayClick = () => {
    const currentMonth = moment().format('YYYY-MM');
    setCurrentYearWithMonth(currentMonth);
    setEndYearWithMonth(currentMonth);
    setURLQuery(currentMonth, currentMonth);
    setInvoiceAlert(false);
    setMonthlyHostsWithoutInvoices([]);
    setYearlyHostsWithoutInvoices([]);
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      const params = new URLSearchParams(location.search);
      const urlStartDate = params.get('startDate');
      const urlEndDate = params.get('endDate');

      let invoices;
      let startMonth, endMonth;
      if (urlStartDate && urlEndDate) {
        // Ensure start date is before or equal to end date
        const startMoment = moment(urlStartDate, 'YYYY-MM');
        const endMoment = moment(urlEndDate, 'YYYY-MM');

        if (startMoment.isAfter(endMoment)) {
          // Swap dates if start is after end
          startMonth = urlEndDate;
          endMonth = urlStartDate;
        } else {
          startMonth = urlStartDate;
          endMonth = urlEndDate;
        }

        // Validate that the URL range doesn't exceed 3 months
        const urlMonthsDiff = Math.abs(moment(endMonth, 'YYYY-MM').diff(moment(startMonth, 'YYYY-MM'), 'months', true));

        if (urlMonthsDiff > 2) {
          // If URL range exceeds 3 months, auto-adjust to 3 months from start date
          endMonth = moment(startMonth, 'YYYY-MM')
            .add(2, 'months')
            .format('YYYY-MM');

          // Show notification about auto-adjustment
          notification.warning({
            message: 'Date Range Adjusted',
            description: `The URL date range exceeded 3 months and has been automatically adjusted to: ${startMonth} to ${endMonth}`,
            duration: 5,
            placement: 'topRight'
          });

          // Update URL with corrected range
          setURLQuery(startMonth, endMonth);
        }

        setCurrentYearWithMonth(startMonth);
        setEndYearWithMonth(endMonth);
      } else if (urlStartDate && !urlEndDate) {
        // If only start date provided, set end date to same month
        startMonth = urlStartDate;
        endMonth = urlStartDate;
        setCurrentYearWithMonth(startMonth);
        setEndYearWithMonth(endMonth);
        setURLQuery(startMonth, endMonth);
      } else if (!urlStartDate && urlEndDate) {
        // If only end date provided, set start date to same month
        startMonth = urlEndDate;
        endMonth = urlEndDate;
        setCurrentYearWithMonth(startMonth);
        setEndYearWithMonth(endMonth);
        setURLQuery(startMonth, endMonth);
      } else {
        // Default to current month
        startMonth = moment().format('YYYY-MM');
        endMonth = moment().format('YYYY-MM');
        setCurrentYearWithMonth(startMonth);
        setEndYearWithMonth(endMonth);
        setURLQuery(startMonth, endMonth);
      }

      try {
        let { monthlyHostsWithoutInvoices, yearlyHostsWithoutInvoices } = await getHostWithNoInvoice(startMonth);
        const allProducts = await getAllProduct();
        const filteredProducts = allProducts.data.filter(
          product => !Array.isArray(product.categoryParent) || !disallowedCategories.includes(product.categoryParent[0])
        );
        console.log(filteredProducts, 'Filtered Products');
        let graphData = await getInoivceGraphData(startMonth, endMonth);
        console.log(graphData, 'Graph Data');
        invoices = await getInvoicesByDate(startMonth);

        // Accumulate values if graphData has more than 1 month
        let currentMonthGraphData;
        if (graphData && graphData.length > 1) {
          // Initialize accumulator object with all possible keys
          currentMonthGraphData = {
            'P-INV Created': 0,
            'Total of P-INV Paid': 0,
            'Total Amount Billed': 0,
            'Total Amount Collected': 0,
            'Total Yearly Subscription Billed': 0,
            'Total Monthly Subscription Billed': 0,
            'Yearly Total Billed': 0,
            'Yearly Total Collected': 0,
            'Monthly Total Billed': 0,
            'Monthly Total Collected': 0,
            'Monthly P-INV Paid': 0,
            'Yearly P-INV Paid': 0,
            'Monthly Total INV & OR Completed': 0,
            'Yearly Total INV & OR Completed': 0,
            'Total INV & OR Completed': 0,
            'Total API Integration Billed': 0,
            'Total API Integration Collected': 0,
            'Total API Integration INV Created': 0,
            'Total API Integration INV Paid': 0,
            'Total API Integration INV & OR Completed': 0,
            'Total Other Product Billed': 0,
            'Total Other Product Collected': 0,
            'Total Other Product INV Created': 0,
            'Total Other Product INV Paid': 0,
            'Total Other Product INV & OR Completed': 0,
            'Total HostProtect Billed': 0,
            'Total HostProtect Collected': 0,
            'Total HostProtect INV Created': 0,
            'Total HostProtect INV Paid': 0,
            'Total HostProtect INV & OR Completed': 0,
            'Total HostProtect Units': 0,
            'Total Subscription Add On': 0,
            'Total DORA': 0,
            'Total Host Manage': 0,
            'Total Host Connect': 0,
            'Monthly Subscription Add On': 0,
            'Monthly DORA': 0,
            'Monthly Host Manage': 0,
            'Monthly Host Connect': 0,
            'Yearly Subscription Add On': 0,
            'Yearly DORA': 0,
            'Yearly Host Manage': 0,
            'Yearly Host Connect': 0
          }; // Accumulate values from all months
          graphData.forEach((monthData, index) => {
            // First, accumulate static keys
            Object.keys(currentMonthGraphData).forEach(key => {
              if (monthData[key] !== undefined && monthData[key] !== null) {
                currentMonthGraphData[key] += monthData[key];

                // Round monetary values to 2 decimal places to avoid floating point precision issues
                if (key.includes('Billed') || key.includes('Collected')) {
                  currentMonthGraphData[key] = Math.round(currentMonthGraphData[key] * 100) / 100;
                }
              }
            });

            // Then, accumulate dynamic product statistics (only if not already processed above)
            filteredProducts.forEach(product => {
              const keySuffix = catalogProductHasIntegrationParent(product) ? INTEGRATION_PRODUCT_STATS_SUFFIX : '';
              const statName = `${product.name}${keySuffix}`;
              const productKeys = [
                `Total ${statName} Billed`,
                `Total ${statName} Collected`,
                `Total ${statName} INV Created`,
                `Total ${statName} INV Paid`,
                `Total ${statName} INV & OR Completed`
              ];

              productKeys.forEach(key => {
                if (!(key in currentMonthGraphData)) {
                  if (!currentMonthGraphData[key]) {
                    currentMonthGraphData[key] = 0;
                  }
                  if (monthData[key] !== undefined && monthData[key] !== null) {
                    currentMonthGraphData[key] += monthData[key];

                    if (key.includes('Billed') || key.includes('Collected')) {
                      currentMonthGraphData[key] = Math.round(currentMonthGraphData[key] * 100) / 100;
                    }
                  }
                }
              });
            });
          });
        } else {
          // Single month logic (existing behavior)
          const currentMonthMoment = moment(startMonth, 'YYYY-MM');
          currentMonthGraphData =
            graphData
              .filter(data => {
                const monthMoment = moment(data.month, 'MMM');
                return monthMoment.month() === currentMonthMoment.month();
              })
              .find(data => data) || {};
        }

        const dynamicProductStats = [];
        filteredProducts.forEach(product => {
          const statName = catalogProductHasIntegrationParent(product) ? `${product.name}${INTEGRATION_PRODUCT_STATS_SUFFIX}` : product.name;
          dynamicProductStats.push(
            { title: `Total ${statName} Billed`, value: currentMonthGraphData[`Total ${statName} Billed`] || 0 },
            { title: `Total ${statName} Collected`, value: currentMonthGraphData[`Total ${statName} Collected`] || 0 },
            { title: `Total ${statName} INV Created`, value: currentMonthGraphData[`Total ${statName} INV Created`] || 0 },
            { title: `Total ${statName} INV Paid`, value: currentMonthGraphData[`Total ${statName} INV Paid`] || 0 },
            { title: `Total ${statName} INV & OR Completed`, value: currentMonthGraphData[`Total ${statName} INV & OR Completed`] || 0 }
          );
        });

        const invoiceStatistics = [
          { title: 'P-INV Created', value: currentMonthGraphData['P-INV Created'] },
          { title: 'Total of P-INV Paid', value: currentMonthGraphData['Total of P-INV Paid'] },
          { title: 'Total Amount Billed', value: currentMonthGraphData['Total Amount Billed'] },
          { title: 'Total Amount Collected', value: currentMonthGraphData['Total Amount Collected'] },
          { title: 'Total Yearly Subscription Billed', value: currentMonthGraphData['Total Yearly Subscription Billed'] },
          { title: 'Total Monthly Subscription Billed', value: currentMonthGraphData['Total Monthly Subscription Billed'] },
          { title: 'Yearly Total Billed', value: currentMonthGraphData['Yearly Total Billed'] },
          { title: 'Yearly Total Collected', value: currentMonthGraphData['Yearly Total Collected'] },
          { title: 'Monthly Total Billed', value: currentMonthGraphData['Monthly Total Billed'] },
          { title: 'Monthly Total Collected', value: currentMonthGraphData['Monthly Total Collected'] },
          { title: 'Monthly P-INV Paid', value: currentMonthGraphData['Monthly P-INV Paid'] },
          { title: 'Yearly P-INV Paid', value: currentMonthGraphData['Yearly P-INV Paid'] },
          { title: 'Monthly Total INV & OR Completed', value: currentMonthGraphData['Monthly Total INV & OR Completed'] },
          { title: 'Yearly Total INV & OR Completed', value: currentMonthGraphData['Yearly Total INV & OR Completed'] },
          { title: 'Total INV & OR Completed', value: currentMonthGraphData['Total INV & OR Completed'] },
          { title: 'Total API Integration Billed', value: currentMonthGraphData['Total API Integration Billed'] },
          { title: 'Total API Integration Collected', value: currentMonthGraphData['Total API Integration Collected'] },
          { title: 'Total API Integration INV Created', value: currentMonthGraphData['Total API Integration INV Created'] },
          { title: 'Total API Integration INV Paid', value: currentMonthGraphData['Total API Integration INV Paid'] },
          { title: 'Total API Integration INV & OR Completed', value: currentMonthGraphData['Total API Integration INV & OR Completed'] },
          { title: 'Total Other Product Billed', value: currentMonthGraphData['Total Other Product Billed'] },
          { title: 'Total Other Product Collected', value: currentMonthGraphData['Total Other Product Collected'] },
          { title: 'Total Other Product INV Created', value: currentMonthGraphData['Total Other Product INV Created'] },
          { title: 'Total Other Product INV Paid', value: currentMonthGraphData['Total Other Product INV Paid'] },
          { title: 'Total Other Product INV & OR Completed', value: currentMonthGraphData['Total Other Product INV & OR Completed'] },
          { title: 'Total HostProtect Billed', value: currentMonthGraphData['Total HostProtect Billed'] },
          { title: 'Total HostProtect Collected', value: currentMonthGraphData['Total HostProtect Collected'] },
          { title: 'Total HostProtect INV Created', value: currentMonthGraphData['Total HostProtect INV Created'] },
          { title: 'Total HostProtect INV Paid', value: currentMonthGraphData['Total HostProtect INV Paid'] },
          { title: 'Total HostProtect INV & OR Completed', value: currentMonthGraphData['Total HostProtect INV & OR Completed'] },
          { title: 'Total HostProtect Units', value: currentMonthGraphData['Total HostProtect Units'] },
          { title: 'Total Subscription Add On', value: currentMonthGraphData['Total Subscription Add On'] },
          { title: 'Total DORA', value: currentMonthGraphData['Total DORA'] },
          { title: 'Total Host Manage', value: currentMonthGraphData['Total Host Manage'] },
          { title: 'Total Host Connect', value: currentMonthGraphData['Total Host Connect'] },
          { title: 'Monthly Subscription Add On', value: currentMonthGraphData['Monthly Subscription Add On'] },
          { title: 'Monthly DORA', value: currentMonthGraphData['Monthly DORA'] },
          { title: 'Monthly Host Manage', value: currentMonthGraphData['Monthly Host Manage'] },
          { title: 'Monthly Host Connect', value: currentMonthGraphData['Monthly Host Connect'] },
          { title: 'Yearly Subscription Add On', value: currentMonthGraphData['Yearly Subscription Add On'] },
          { title: 'Yearly DORA', value: currentMonthGraphData['Yearly DORA'] },
          { title: 'Yearly Host Manage', value: currentMonthGraphData['Yearly Host Manage'] },
          { title: 'Yearly Host Connect', value: currentMonthGraphData['Yearly Host Connect'] },
          // Add dynamic product statistics
          ...dynamicProductStats
        ];
        if (monthlyHostsWithoutInvoices.length > 0 || yearlyHostsWithoutInvoices.length > 0) {
          setInvoiceAlert(true);
        }
        setMonthlyHostsWithoutInvoices(monthlyHostsWithoutInvoices);
        setYearlyHostsWithoutInvoices(yearlyHostsWithoutInvoices);
        setInvoiceStatisticsData(invoiceStatistics);
        setInvoiceGraphData(graphData);
        setAllProducts(filteredProducts);
      } catch (ex) {
        console.log(ex);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [location.search]);

  return (
    <BodyContainer>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <div style={{ fontWeight: 'bold' }}>Range :</div>{' '}
        <DatePicker.RangePicker
          disabled={isLoading}
          format="YYYY-MM"
          placeholder={['Start Month', 'End Month']}
          value={[moment(currentYearWithMonth), moment(endYearWithMonth)]}
          onOpenChange={status => {
            setOpen(status);
            setMode(['month', 'month']);
          }}
          open={open}
          mode={mode}
          onPanelChange={handlePanelChange}
        />
        <Button type="primary" onClick={handleTodayClick} disabled={isLoading} id="today-date-button3-report">
          Today
        </Button>
      </div>
      <MainContainer>
        <Alert style={{ marginBottom: '10px' }} message={`Dashboard doesn't include manual created invoices`} type="info" showIcon />
        {invoiceAlert && (
          <div onClick={handleAlertClick} style={{ cursor: 'pointer' }}>
            <Alert
              style={{ marginBottom: '10px' }}
              message={`Alert: ${monthlyHostsWithoutInvoices.length +
                yearlyHostsWithoutInvoices.length} hosts without subscription invoices for the current month.`}
              type="warning"
              showIcon
            />
          </div>
        )}
        <StatisticsCard data={invoiceStatisticsData} loading={isLoading} />
        <TotalBilledCollectedGraph data={invoiceGraphData} height={350} loading={isLoading} />
        <ProductsTable data={invoiceStatisticsData} products={allProducts} loading={isLoading} />
        <AdditionalProductGraph data={invoiceGraphData} products={allProducts} loading={isLoading} />
      </MainContainer>
      <Modal
        title="Hosts Without Invoices"
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseModal}>
            Close
          </Button>
        ]}
      >
        <Row gutter={16}>
          <Col span={12}>
            <h3>Monthly Hosts</h3>
            {monthlyHostsWithoutInvoices.length > 0 ? (
              <ul>
                {monthlyHostsWithoutInvoices.map(host => (
                  <li key={host._id}>
                    <a
                      onClick={() => {
                        // Create the formatted invoice object
                        const formattedInvoice = {
                          host: host._id,
                          invoiceNo: host.invoiceNo || null,
                          invoiceDate: moment().format('YYYY-MM-DD'),
                          invoiceDueDate: moment().format('YYYY-MM-DD'),
                          amount: null,
                          paymentStatus: 'unpaid',
                          files: [],
                          officialInvoice: [],
                          officialReceipt: []
                        };
                        const isEditClicked = false;
                        const record = null;
                        const hostEmail = host.invoiceEmail;

                        // Navigate to the preview page
                        history.push({
                          pathname: `${buildAPBillingInvoiceUri()}/${host._id}/${currentYearWithMonth}/preview`,
                          state: { formattedInvoice, isEditClicked, record, hostEmail }
                        });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {host.name}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No monthly hosts without invoices.</p>
            )}
          </Col>
          <Col span={12}>
            <h3>Yearly Hosts</h3>
            {yearlyHostsWithoutInvoices.length > 0 ? (
              <ul>
                {yearlyHostsWithoutInvoices.map(host => (
                  <li key={host.id}>
                    <a
                      onClick={() => {
                        // Create the formatted invoice object
                        const formattedInvoice = {
                          host: host._id,
                          invoiceNo: host.invoiceNo || null,
                          invoiceDate: moment().format('YYYY-MM-DD'),
                          invoiceDueDate: moment().format('YYYY-MM-DD'),
                          amount: null,
                          paymentStatus: 'unpaid',
                          files: [],
                          officialInvoice: [],
                          officialReceipt: []
                        };
                        const isEditClicked = false;
                        const record = null;
                        const hostEmail = host.invoiceEmail;

                        // Navigate to the preview page
                        history.push({
                          pathname: `${buildAPBillingInvoiceUri()}/${host._id}/${currentYearWithMonth}/preview`,
                          state: { formattedInvoice, isEditClicked, record, hostEmail }
                        });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {host.name}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No yearly hosts without invoices.</p>
            )}
          </Col>
        </Row>
      </Modal>
    </BodyContainer>
  );
};

export default InvoiceDashboard;
